import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useState, useMemo, useRef } from "react";
import { AgentContext } from "../types/agentConfig";
import { useGenericAgentConversations } from "../hooks/useGenericAgentConversations";
import { useGenericAgentChat } from "../hooks/useGenericAgentChat";
import { ChatInput } from "./ChatInput";
import { GenericChatMessages } from "./GenericChatMessages";
import { ConversationList } from "./ConversationList";
import { ConversationHistoryButton } from "./ConversationHistoryButton";
import { NewChatButton } from "./NewChatButton";
import { AIDisclaimer } from "../../../shared/components";

interface GenericAgentChatTabProps {
  context: AgentContext;
}

export const GenericAgentChatTab: React.FC<GenericAgentChatTabProps> = ({
  context,
}) => {
  const [selectedConversation, setSelectedConversation] = useState<
    string | null
  >(null);
  const [isSideMenuCollapsed, setIsSideMenuCollapsed] = useState(true);
  const [isLocallyLoading, setIsLocallyLoading] = useState(false);
  const autoInitiatedRef = useRef(false);

  const { conversations, loadingStatus, error } =
    useGenericAgentConversations(context);
  const {
    createConversation,
    addMessage,
    addSystemInitiationMessage,
    getAgentConfig,
  } = useGenericAgentChat(context);

  // Set the first conversation by default when conversations are loaded
  React.useEffect(() => {
    if (conversations.length > 0 && !selectedConversation) {
      setSelectedConversation(conversations[0].id);
    }
  }, [conversations, selectedConversation]);

  // Clear local loading state when conversation changes
  React.useEffect(() => {
    setIsLocallyLoading(false);
  }, [selectedConversation]);

  // Auto-initiate conversation for agents when no conversations exist
  React.useEffect(() => {
    const shouldAutoInitiate =
      loadingStatus === "succeeded" &&
      conversations.length === 0 &&
      !autoInitiatedRef.current;

    if (shouldAutoInitiate) {
      autoInitiatedRef.current = true;

      // Create initial conversation with agent message
      const initiateConversation = async () => {
        try {
          let conversationTitle: string;
          let initiationMessage: string;

          switch (context.agentType) {
            case "document":
              conversationTitle = "Document Analysis";
              initiationMessage =
                "<The user just navigated to this document after uploading it to Stratify. Give the user a brief overview about this document. You don't need to include all the information, just the documents purpose and a summary of what it contains.>";
              break;
            case "property":
              conversationTitle = "Property Management";
              initiationMessage =
                "<The user just navigated to a property management for the first time. Summarize the details you know about the documents of the property, how many there are, and what they are about. Then ask how you can assist. Be concise.>";
              break;
            case "test":
              conversationTitle = "Test Conversation";
              initiationMessage =
                "Please introduce yourself as a test assistant and explain your capabilities, then ask how you can help.";
              break;
            default:
              // Generic fallback
              conversationTitle = "New Conversation";
              initiationMessage =
                "Please introduce yourself and explain how you can help, then ask what the user would like to know.";
          }

          const conversationId = await createConversation(conversationTitle);
          // Add a special system initiation message that triggers the agent to introduce itself
          await addSystemInitiationMessage(conversationId, initiationMessage);
          setSelectedConversation(conversationId);
        } catch (error) {
          console.error("Failed to auto-initiate conversation:", error);
          autoInitiatedRef.current = false; // Reset to allow retry
        }
      };

      initiateConversation();
    }
  }, [
    context.agentType,
    loadingStatus,
    conversations.length,
    createConversation,
    addSystemInitiationMessage,
  ]);

  const selectedConversationData = useMemo(() => {
    return conversations.find((conv) => conv.id === selectedConversation);
  }, [conversations, selectedConversation]);

  // Clear local loading state when backend conversation metadata indicates completion
  React.useEffect(() => {
    if (selectedConversationData && isLocallyLoading) {
      // If the conversation exists and is not loading on the backend, clear local loading
      if (!selectedConversationData.metadata.isLoading && !selectedConversationData.metadata.isWorking) {
        setIsLocallyLoading(false);
      }
    }
  }, [selectedConversationData?.metadata.isLoading, selectedConversationData?.metadata.isWorking, isLocallyLoading]);

  const handleNewMessage = async (text: string) => {
    let conversationId = selectedConversation;

    // Set local loading state immediately for user feedback
    setIsLocallyLoading(true);

    try {
      if (!conversationId) {
        conversationId = await createConversation(text);
        await addMessage(conversationId, text);
        setSelectedConversation(conversationId);
      } else {
        await addMessage(conversationId, text);
      }
    } catch (error) {
      // Reset loading state on error
      setIsLocallyLoading(false);
      throw error;
    }
  };

  const handleNewConversation = async () => {
    const conversationId = await createConversation("New Conversation");
    setSelectedConversation(conversationId);
    setIsSideMenuCollapsed(true);
  };

  const handleSelectConversation = (conversationId: string | null) => {
    setSelectedConversation(conversationId);
    setIsSideMenuCollapsed(true);
  };

  const agentConfig = getAgentConfig();

  // Loading state
  if (loadingStatus === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Error loading conversations: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flex: 1, minHeight: 0 }}>
      {/* Side menu for conversation history */}
      <Box
        sx={{
          width: isSideMenuCollapsed ? 0 : 300,
          transition: "width 0.3s",
          overflow: "hidden",
          borderRight: isSideMenuCollapsed ? "none" : "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {agentConfig.displayName}
          </Typography>
          <NewChatButton onClick={handleNewConversation} />
          <ConversationList
            conversations={conversations.map((conv) => ({
              id: conv.id,
              metadata: conv.metadata,
            }))}
            selectedId={selectedConversation}
            onSelect={handleSelectConversation}
          />
        </Box>
      </Box>

      {/* Main chat area */}
      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        {/* Header with conversation history button */}
        <Box
          sx={{
            p: 1,
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
          }}
        >
          <ConversationHistoryButton
            isCollapsed={isSideMenuCollapsed}
            onToggle={() => setIsSideMenuCollapsed(!isSideMenuCollapsed)}
          />
          <Typography variant="h6" sx={{ ml: 2 }}>
            {selectedConversationData?.metadata.title ||
              `${agentConfig.displayName} Chat`}
          </Typography>
        </Box>

        {/* Messages area */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {selectedConversation && selectedConversationData ? (
            <GenericChatMessages
              context={context}
              conversation={selectedConversationData}
              isLocallyLoading={isLocallyLoading}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Typography color="textSecondary">
                How can I help you today?
              </Typography>
            </Box>
          )}
        </Box>

        {/* Input area */}
        <Box sx={{ p: 2, pb: 1, borderTop: "1px solid #e0e0e0" }}>
          <ChatInput
            onSendMessage={handleNewMessage}
            disabled={
              !selectedConversation && conversations.length === 0
                ? false
                : false
            }
            placeholder={`Message ${agentConfig.displayName}...`}
          />
          <AIDisclaimer sx={{ mt: 1 }} />
        </Box>
      </Box>
    </Box>
  );
};
